import { FC, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Container,
  IconWrapper,
  Title,
  Message,
  CountdownWrapper,
  CountdownNumber,
  CountdownLabel,
  DeviceInfo,
  DeviceId,
  ServerUrl,
} from './styles';
import OrientationControls from '~/components/OrientationControls';
import { resolveApiBaseUrl } from '~/services/embedded/hostConfig';

// Helper function to get API URL for troubleshooting.
// Mesma fonte que o sync usa — inclusive a URL que o APK host passou no
// embedded. A tela de diagnóstico mostrando um servidor diferente do que o
// player realmente chama é pior que não mostrar nada.
const getApiUrl = (): string => resolveApiBaseUrl();

/**
 * Versão do app pra exibir no footer das telas de fallback.
 *
 * Injetada via {@code vite.config.ts} (`define: { 'import.meta.env.VITE_APP_VERSION' }`)
 * lendo o `version` do package.json no build-time. Funciona pra build de
 * release (Android/Tizen) e dev. Quando ausente (ex: vitest com env stub
 * incompleto), cai pra string vazia.
 *
 * 2026-05-24 — Pedido em campo: TODA tela azul (NoContent) deve mostrar a
 * versão pra facilitar diagnóstico remoto. "A TV travou em 'Aguardando
 * aprovação' — qual versão tá rodando?" deve ser respondível olhando a
 * tela, sem precisar abrir o painel INFO debug.
 */
const APP_VERSION =
  (import.meta.env.VITE_APP_VERSION as string | undefined) || '';

/**
 * Formata um timestamp ISO em hora local (HH:MM:SS, 24h pt-BR).
 *
 * Usado pra exibir "Última sync" no footer das telas azuis — operador
 * em campo identifica rapidamente se o player está fazendo polling ou
 * travou.
 *
 * @param iso  timestamp ISO 8601 ou null
 * @returns    hora local formatada, ou "—" se ausente/inválido
 */
const formatLocalTime = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleTimeString('pt-BR', { hour12: false });
  } catch {
    return '—';
  }
};

export type NoContentReason =
  | 'no_content'
  | 'offline_no_cache' // [LEGACY] generic offline + sem cache. Mantido pra compat;
  //                        novo código deve usar offline_no_internet / server_unreachable.
  | 'offline_no_internet' // ❌ internet (não pinga google.com nem [server])
  | 'server_unreachable' // ⚠️ wifi OK, server inacessível (firewall/DNS/server)
  | 'connection_failed'
  | 'auth_error'
  | 'sync_error'
  | 'player_blocked';

interface NoContentProps {
  countdown: number;
  /** Wall-clock target from syncStore — display derives from this when set. */
  retryTargetAt?: number | null;
  isOnline: boolean;
  lastSyncTime: string | null;
  error: string | null;
  deviceId: string;
  tenantName?: string;
  reason?: NoContentReason;
  serverUrl?: string;
  registrationStatus?: string;
  backgroundUrl?: string; // Custom background image/video URL (data URL or remote)
}

// Wi-Fi Off icon for offline state
const WifiOffIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M22.99 9C19.15 5.16 13.8 3.76 8.84 4.78l2.52 2.52c3.47-.17 6.99 1.05 9.63 3.7l2-2zm-4 4c-1.29-1.29-2.84-2.13-4.49-2.56l3.53 3.53.96-.97zM2 3.05L5.07 6.1C3.6 6.82 2.22 7.78 1 9l1.99 2c1.24-1.24 2.67-2.16 4.2-2.77l2.24 2.24C7.81 10.89 6.27 11.73 5 13l2 2c1.35-1.35 3.11-2.04 4.89-2.06l7.08 7.08 1.42-1.42L3.42 1.63 2 3.05zM9 17l3 3 3-3c-1.65-1.66-4.34-1.66-6 0z"
      fill="currentColor"
    />
  </svg>
);

// Media player icon for normal state
const MediaIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H8V4h12v12zM12 5.5v9l6-4.5-6-4.5z"
      fill="currentColor"
    />
  </svg>
);

// Lock icon for blocked state
const BlockedIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6zm9 14H6V10h12v10zm-6-3c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2z"
      fill="currentColor"
    />
  </svg>
);

const NoContent: FC<NoContentProps> = observer(
  ({
    countdown,
    retryTargetAt,
    error,
    deviceId,
    tenantName,
    lastSyncTime,
    reason = 'no_content',
    serverUrl,
    registrationStatus,
    backgroundUrl,
  }) => {
    // URL do servidor pra exibir nas mensagens de diagnóstico em campo.
    // Preferência: prop explícita > env vars (mesma ordem do SyncService).
    const resolvedServerUrl = serverUrl || getApiUrl();

    // Get title and message based on reason
    const getContent = () => {
      switch (reason) {
        case 'offline_no_internet':
          // Cenário A — sem internet de verdade (probes pra Google + server
          // ambos falharam). Problema é Wi-Fi / rede da loja.
          return {
            icon: <WifiOffIcon />,
            title: 'Sem conexão com a internet',
            message: `Não foi possível alcançar a internet nem o servidor (${resolvedServerUrl}).`,
            submessage: 'Verifique o Wi-Fi do dispositivo ou a rede da loja.',
            countdownLabel: 'Tentando reconectar em',
          };
        case 'server_unreachable':
          // Cenário B — Wi-Fi OK, mas o servidor Efflux está inacessível.
          // Causas típicas: firewall do cliente bloqueando, DNS quebrado,
          // server fora do ar, ou rota errada do tenant. Mensagem aponta
          // a TI do cliente pra onde olhar.
          return {
            icon: <WifiOffIcon />,
            title: 'Sem acesso ao servidor Efflux',
            message: `O servidor (${resolvedServerUrl}) está inacessível, mesmo com internet OK.`,
            submessage: 'Verifique firewall, DNS ou status do servidor.',
            countdownLabel: 'Tentando reconectar em',
          };
        case 'offline_no_cache':
          // [LEGACY] Compat com chamadas antigas — quem ainda manda esse
          // reason cai num default "offline genérico + sem cache".
          return {
            icon: <WifiOffIcon />,
            title: 'Sem conexão com a internet',
            message: `Sem internet e sem conteúdo salvo (servidor: ${resolvedServerUrl}).`,
            submessage: 'Conecte à rede para baixar conteúdo.',
            countdownLabel: 'Tentando reconectar em',
          };
        case 'connection_failed':
          // Mantido — usado quando existe cache mas falha o sync. UX antiga
          // genérica; preferir server_unreachable quando possível.
          return {
            icon: <WifiOffIcon />,
            title: 'Não foi possível conectar ao servidor',
            message: `Falha ao sincronizar com ${resolvedServerUrl}.`,
            submessage: null,
            countdownLabel: 'Tentando reconectar em',
          };
        case 'auth_error': {
          // 🆕 Detect if device is in automatic registration process
          const isRegistering =
            registrationStatus === 'registering' ||
            registrationStatus === 'initializing';
          const isPendingApproval = registrationStatus === 'pending_approval';

          if (isRegistering) {
            return {
              icon: <MediaIcon />,
              title: 'Registrando dispositivo...',
              message:
                'Aguarde enquanto o dispositivo é registrado no servidor.',
              submessage: null,
              countdownLabel: 'Registrando',
            };
          }

          if (isPendingApproval) {
            return {
              icon: <MediaIcon />,
              title: 'Aguardando aprovação',
              message:
                'Dispositivo registrado. Aguardando aprovação do administrador.',
              submessage: 'Aprove este dispositivo no painel de administração.',
              countdownLabel: 'Verificando aprovação em',
            };
          }

          return {
            icon: <MediaIcon />,
            title: 'Dispositivo não autorizado',
            message: error || 'Entre em contato com o administrador.',
            submessage: null,
            countdownLabel: 'Próxima tentativa em',
          };
        }
        case 'sync_error':
          return {
            icon: <MediaIcon />,
            title: 'Erro ao sincronizar',
            message: error || 'Ocorreu um erro ao buscar conteúdo.',
            submessage: null,
            countdownLabel: 'Próxima tentativa em',
          };
        case 'player_blocked':
          return {
            icon: <BlockedIcon />,
            title: 'Player bloqueado',
            message:
              'Este player foi bloqueado por um administrador e não está exibindo conteúdo.',
            submessage:
              'Entre em contato com o administrador para reativar o dispositivo.',
            countdownLabel: 'Verificando status em',
          };
        default: // no_content
          return {
            icon: <MediaIcon />,
            title: 'Aguardando conteúdo',
            message: 'Nenhum conteúdo disponível no momento.',
            submessage: null,
            countdownLabel: 'Próxima tentativa em',
          };
      }
    };

    const content = getContent();

    // 2026-05-31 — Countdown timestamp-based standalone.
    //
    // <p>Renato observou na v1.0.90: número trava em 28→26 por 2 minutos.
    // Causa: useEffect com deps {@code [targetTs, secondsLeft]} re-rodava
    // a cada tick (4×/s), matando+recriando setInterval em loop. Em Tizen
    // WebView isso causa drift acumulado.</p>
    //
    // <p>Fix: setup do interval UMA ÚNICA VEZ no mount (deps vazios). O
    // {@code targetTs} é {@code useRef} — mutável sem trigger de re-render.
    // {@code secondsLeft} (useState) só atualiza quando o valor muda de
    // verdade. Loop interno reseta {@code targetTs.current} sem disparar
    // re-render do effect.</p>
    const FALLBACK_INTERVAL = 30;
    const targetTsRef = useRef<number>(Date.now() + FALLBACK_INTERVAL * 1000);
    const [secondsLeft, setSecondsLeft] = useState(FALLBACK_INTERVAL);

    // Quando syncStore reinicia retry, alinha o ref local ao target autoritativo.
    useEffect(() => {
      if (retryTargetAt != null && retryTargetAt > 0) {
        targetTsRef.current = retryTargetAt;
      }
    }, [retryTargetAt]);

    useEffect(() => {
      const tick = () => {
        const target =
          retryTargetAt != null && retryTargetAt > 0
            ? retryTargetAt
            : targetTsRef.current;
        const remaining = Math.max(0, Math.ceil((target - Date.now()) / 1000));
        if (remaining <= 0 && (retryTargetAt == null || retryTargetAt <= 0)) {
          targetTsRef.current = Date.now() + FALLBACK_INTERVAL * 1000;
          setSecondsLeft(FALLBACK_INTERVAL);
        } else {
          setSecondsLeft(remaining);
        }
      };
      const interval = setInterval(tick, 500);
      tick();
      return () => clearInterval(interval);
    }, [retryTargetAt]);

    // Display sempre wall-clock — não depende do MobX retryCountdown que
    // só atualiza quando o event loop consegue rodar ticks do sync.store.
    const displayCountdown =
      retryTargetAt != null && retryTargetAt > 0
        ? secondsLeft
        : countdown > 0
          ? countdown
          : secondsLeft;

    return (
      <Container $hasBackground={!!backgroundUrl}>
        {/* Custom Background Image */}
        {backgroundUrl &&
          (backgroundUrl.startsWith('data:video') ||
          backgroundUrl.endsWith('.mp4') ? (
            <video
              src={backgroundUrl}
              autoPlay
              loop
              muted
              playsInline
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                zIndex: 0,
              }}
            />
          ) : (
            <img
              src={backgroundUrl}
              alt="Background"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                zIndex: 0,
              }}
            />
          ))}

        {/* Content Overlay */}
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <IconWrapper>{content.icon}</IconWrapper>

          <Title $hasBackground={!!backgroundUrl}>{content.title}</Title>

          <Message $hasBackground={!!backgroundUrl}>{content.message}</Message>

          {content.submessage && (
            <Message
              $hasBackground={!!backgroundUrl}
              style={{ marginTop: '8px', opacity: 0.8 }}
            >
              {content.submessage}
            </Message>
          )}

          {/* Bloco único: "Sincronizando..." + countdown logo abaixo.
              Antes eram 2 CountdownWrappers consecutivos, cada um com
              margin-bottom: 48px + DeviceInfo margin-top: 32px = 128px de
              gap entre o último elemento e a versão. Renato pediu pra
              colar (2026-05-31). */}
          <CountdownWrapper>
            <CountdownLabel $hasBackground={!!backgroundUrl}>
              Sincronizando...
            </CountdownLabel>
            <CountdownLabel
              $hasBackground={!!backgroundUrl}
              style={{ marginTop: 8 }}
            >
              {content.countdownLabel}
            </CountdownLabel>
            <CountdownNumber>{displayCountdown}</CountdownNumber>
            <CountdownLabel $hasBackground={!!backgroundUrl}>
              segundos
            </CountdownLabel>
          </CountdownWrapper>

          <DeviceInfo $hasBackground={!!backgroundUrl}>
            {/*
              2026-05-24 — Versão SEMPRE no topo. Pedido em campo: troubleshooting
              remoto fica viável sem precisar abrir o painel INFO debug.
              VITE_APP_VERSION é injetado via vite.config.ts (build-time).
            */}
            {APP_VERSION && <DeviceId>Versão: v{APP_VERSION}</DeviceId>}
            <DeviceId>Device: {deviceId}</DeviceId>
            {tenantName && (
              <DeviceId style={{ opacity: 0.7 }}>Tenant: {tenantName}</DeviceId>
            )}
            <ServerUrl $hasBackground={!!backgroundUrl}>
              Servidor: {resolvedServerUrl}
            </ServerUrl>
            {/*
              Última sync apenas — o countdown no topo já indica próxima
              tentativa. "Próxima sync: agora" virou redundante (Renato,
              2026-05-31).
            */}
            <DeviceId style={{ opacity: 0.7 }}>
              Última sync: {formatLocalTime(lastSyncTime)}
            </DeviceId>
          </DeviceInfo>

          {/*
            2026-06-05 — Botões Paisagem/Retrato pra o operador ajustar a
            orientação na mão quando a auto-detecção não acerta (ex: tablet
            montado em retrato reconhecido como paisagem). Só aparece em
            Android/Browser (gate supportsOrientationChange — TV é fixo).
          */}
          <OrientationControls />
        </div>
      </Container>
    );
  },
);

export default NoContent;
